/**
 * CRS Gap Analysis Page - cross-entity view of the most common gaps.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Loader2, Target } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import capitalReadinessService from '../services/capitalReadinessService';
import {
  CapitalReadinessScore,
  CRS_DIMENSIONS,
} from '../types/capitalReadiness';

const DIMENSION_LABELS: Record<string, string> = {
  financial_preparedness: 'Financial Preparedness',
  market_validation: 'Market Validation',
  team_strength: 'Team Strength',
  legal_compliance: 'Legal Compliance',
  pitch_quality: 'Pitch Quality',
  traction_metrics: 'Traction Metrics',
  investor_readiness: 'Investor Readiness',
};

const SEV_ORDER: Array<'critical' | 'high' | 'medium' | 'low'> = [
  'critical',
  'high',
  'medium',
  'low',
];

const SEV_COLORS: Record<string, string> = {
  critical: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-amber-500',
  low: 'bg-gray-400',
};

const CRSGapAnalysisPage: React.FC = () => {
  const [scores, setScores] = useState<CapitalReadinessScore[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const res = await capitalReadinessService.fetchScores({ size: 500 });
      if (res.error) {
        toast.error(res.error);
      } else {
        setScores(res.data?.scores || []);
      }
      setLoading(false);
    };
    load();
  }, []);

  // Aggregate: dimension -> severity -> count
  const agg = useMemo(() => {
    const map: Record<string, Record<string, number>> = {};
    CRS_DIMENSIONS.forEach((d) => {
      map[d] = { critical: 0, high: 0, medium: 0, low: 0 };
    });
    scores.forEach((s) => {
      (s.gaps || []).forEach((g) => {
        const dim = String(g.dimension);
        if (!map[dim]) {
          map[dim] = { critical: 0, high: 0, medium: 0, low: 0 };
        }
        const sev = g.severity as string;
        map[dim][sev] = (map[dim][sev] || 0) + 1;
      });
    });
    return map;
  }, [scores]);

  const dimensions = useMemo(() => {
    const keys = Object.keys(agg);
    return keys
      .map((d) => {
        const counts = agg[d];
        const total = SEV_ORDER.reduce((s, sev) => s + (counts[sev] || 0), 0);
        return { d, counts, total };
      })
      .sort((a, b) => b.total - a.total);
  }, [agg]);

  const maxTotal = Math.max(1, ...dimensions.map((d) => d.total));
  const totalGaps = dimensions.reduce((s, d) => s + d.total, 0);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <Link
          to="/capital-readiness"
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to capital readiness
        </Link>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 inline-flex items-center">
              <Target className="w-6 h-6 mr-2 text-blue-600" />
              Gap analysis
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Aggregated gaps across {scores.length} scored entit
              {scores.length === 1 ? 'y' : 'ies'} ({totalGaps} total gap
              {totalGaps === 1 ? '' : 's'}).
            </p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-6">
          {loading ? (
            <div className="flex items-center text-sm text-gray-600">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Loading...
            </div>
          ) : scores.length === 0 ? (
            <p className="text-sm text-gray-500">
              No scored entities yet. Run some scores first.
            </p>
          ) : (
            <div className="space-y-3">
              {/* Legend */}
              <div className="flex flex-wrap gap-3 text-xs text-gray-600 mb-4">
                {SEV_ORDER.map((sev) => (
                  <div key={sev} className="inline-flex items-center">
                    <span
                      className={`w-3 h-3 rounded mr-1.5 ${SEV_COLORS[sev]}`}
                    />
                    <span className="capitalize">{sev}</span>
                  </div>
                ))}
              </div>

              {dimensions.map(({ d, counts, total }) => {
                const pct = (total / maxTotal) * 100;
                return (
                  <div key={d}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-medium text-gray-800">
                        {DIMENSION_LABELS[d] ||
                          d.replace(/_/g, ' ')}
                      </span>
                      <span className="text-gray-600">
                        {total} gap{total === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div
                        className="flex h-6 rounded overflow-hidden bg-gray-100"
                        style={{ width: `${pct}%`, minWidth: total > 0 ? 40 : 8 }}
                      >
                        {SEV_ORDER.map((sev) => {
                          const c = counts[sev] || 0;
                          if (c === 0) return null;
                          const segPct = total > 0 ? (c / total) * 100 : 0;
                          return (
                            <div
                              key={sev}
                              className={`${SEV_COLORS[sev]} flex items-center justify-center text-[10px] text-white font-medium`}
                              style={{ width: `${segPct}%` }}
                              title={`${sev}: ${c}`}
                            >
                              {segPct > 15 ? c : ''}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="mt-1 text-[11px] text-gray-500 flex flex-wrap gap-3">
                      {SEV_ORDER.map((sev) => (
                        <span key={sev} className="capitalize">
                          {sev}: {counts[sev] || 0}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CRSGapAnalysisPage;
